type StoreIndex = {
  name: string;
  keyPath: string | string[];
  options?: IDBIndexParameters;
};

type StoreConfig = {
  name: string;
  keyPath?: string;
  autoIncrement?: boolean;
  indexes?: StoreIndex[];
};

export default class IndexedDBManager {
  private dbName: string;
  private dbVersion: number;
  private storeConfigs: StoreConfig[];
  private db: IDBDatabase | null = null;

  constructor(dbName: string, dbVersion: number, storeConfigs: StoreConfig[]) {
    this.dbName = dbName;
    this.dbVersion = dbVersion;
    this.storeConfigs = storeConfigs;
  }

  private createObjectStoreWithIndexes(db: IDBDatabase, config: StoreConfig) {
    const store = db.createObjectStore(config.name, {
      keyPath: config.keyPath,
      autoIncrement: config.autoIncrement,
    });

    config.indexes?.forEach((index) => {
      store.createIndex(index.name, index.keyPath, index.options || {});
    });
  }

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event) => {
        const db = request.result;
        // console.log(
        //   "onupgradeneeded triggered, version:",
        //   event.oldVersion,
        //   "→",
        //   event.newVersion
        // );
        try {
          this.storeConfigs.forEach((config) => {
            if (!db.objectStoreNames.contains(config.name)) {
              this.createObjectStoreWithIndexes(db, config);
            }
          });
        } catch (err) {
          console.error("❌ Error in onupgradeneeded:", err);
          reject(err); // reject here if creation fails
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onerror = () => {
        console.error("❌ IndexedDB open failed:", request.error);
        reject(request.error);
      };

      request.onblocked = () => {
        console.warn("⚠️ IndexedDB blocked. Close other tabs.");
      };
    });
  }

  async createStore(storeConfig: StoreConfig): Promise<void> {
    this.dbVersion += 1;
    this.storeConfigs.push(storeConfig);
    this.db?.close();

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(storeConfig.name)) {
          this.createObjectStoreWithIndexes(db, storeConfig);
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onerror = () => reject(request.error);
    });
  }

  private getStore(
    storeName: string,
    mode: IDBTransactionMode
  ): IDBObjectStore {
    if (!this.db) throw new Error("Database not initialized");
    const tx = this.db.transaction(storeName, mode);
    return tx.objectStore(storeName);
  }

  async add<T>(storeName: string, value: T): Promise<IDBValidKey> {
    return new Promise((resolve, reject) => {
      try {
        const store = this.getStore(storeName, "readwrite");
        const request = store.add(value);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async clearStore(storeName: string): Promise<void> {
    try {
      await this.init();
      const store = this.getStore(storeName, "readwrite");
      const request = store.clear();
      return new Promise<void>((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("clearStore error:", error);
      throw error;
    }
  }

  async put<T>(storeName: string, value: T): Promise<IDBValidKey> {
    return new Promise((resolve, reject) => {
      try {
        const store = this.getStore(storeName, "readwrite");
        const request = store.put(value);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async get<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      try {
        const store = this.getStore(storeName, "readonly");
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async getAll<T>(storeName: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      try {
        const store = this.getStore(storeName, "readonly");
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async delete(storeName: string, key: IDBValidKey): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const store = this.getStore(storeName, "readwrite");
        const request = store.delete(key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async searchByIndex<T>(
    storeName: string,
    indexName: string,
    queryValue: IDBValidKey
  ): Promise<T[]> {
    return new Promise((resolve, reject) => {
      try {
        const store = this.getStore(storeName, "readonly");
        const index = store.index(indexName);
        const request = index.getAll(queryValue);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      } catch (err) {
        reject(err);
      }
    });
  }

  async findByCompoundIndex<T>(
    storeName: string,
    indexName: string,
    keyTuple: [any, any]
  ): Promise<T[]> {
    await this.init();
    const store = this.getStore(storeName, "readonly");

    return new Promise((resolve, reject) => {
      const index = store.index(indexName);
      const range = IDBKeyRange.only(keyTuple);
      const request = index.getAll(range);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async createIfNotExists<T extends Record<string, any>>(
    storeName: string,
    matchIndexOrKey: string,
    matchValue: any,
    data: T
  ): Promise<{ created: boolean; existing?: T }> {
    try {
      await this.init();

      let existing: T[] = [];

      const store = await this.getStore(storeName, "readonly");

      if (store.indexNames.contains(matchIndexOrKey)) {
        const index = store.index(matchIndexOrKey);
        const request = index.get(matchValue);
        existing = await new Promise<T[]>((resolve, reject) => {
          request.onsuccess = () =>
            resolve(request.result ? [request.result] : []);
          request.onerror = () => reject(request.error);
        });
      } else {
        const request = store.get(matchValue);
        existing = await new Promise<T[]>((resolve, reject) => {
          request.onsuccess = () =>
            resolve(request.result ? [request.result] : []);
          request.onerror = () => reject(request.error);
        });
      }

      if (existing.length > 0) {
        return { created: false, existing: existing[0] };
      }

      await this.add(storeName, data);
      return { created: true };
    } catch (error) {
      console.error("createIfNotExists error:", error);
      throw error;
    }
  }

  async deleteByIndexOrKey(
    storeName: string,
    matchIndexOrKey: string,
    matchValue: any
  ): Promise<number> {
    try {
      await this.init();

      const store = await this.getStore(storeName, "readwrite");
      const keysToDelete: IDBValidKey[] = [];

      if (store.indexNames.contains(matchIndexOrKey)) {
        const index = store.index(matchIndexOrKey);
        const request = index.openCursor(IDBKeyRange.only(matchValue));

        await new Promise<void>((resolve, reject) => {
          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result;
            if (cursor) {
              keysToDelete.push(cursor.primaryKey);
              cursor.continue();
            } else {
              resolve();
            }
          };
          request.onerror = () => reject(request.error);
        });
      } else {
        keysToDelete.push(matchValue);
      }

      for (const key of keysToDelete) {
        await new Promise<void>((resolve, reject) => {
          const deleteRequest = store.delete(key);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        });
      }

      return keysToDelete.length;
    } catch (error) {
      console.error("deleteByIndexOrKey error:", error);
      throw error;
    }
  }

  async updateByIndexOrKey<T extends Record<string, any>>(
    storeName: string,
    matchIndexOrKey: string,
    matchValue: any,
    updatedData: Partial<T>
  ): Promise<number> {
    try {
      await this.init();
      const store = await this.getStore(storeName, "readwrite");
      const updatedRecords: number[] = [];

      if (store.indexNames.contains(matchIndexOrKey)) {
        const index = store.index(matchIndexOrKey);
        const request = index.openCursor(IDBKeyRange.only(matchValue));

        await new Promise<void>((resolve, reject) => {
          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>)
              .result;
            if (cursor) {
              const currentValue = cursor.value;
              const updatedValue = { ...currentValue, ...updatedData };
              cursor.update(updatedValue);
              updatedRecords.push(1);
              cursor.continue();
            } else {
              resolve();
            }
          };
          request.onerror = () => reject(request.error);
        });
      } else {
        const getRequest = store.get(matchValue);
        const value = await new Promise<T | undefined>((resolve, reject) => {
          getRequest.onsuccess = () => resolve(getRequest.result);
          getRequest.onerror = () => reject(getRequest.error);
        });

        if (value) {
          const updatedValue = { ...value, ...updatedData };
          await new Promise<void>((resolve, reject) => {
            const putRequest = store.put(updatedValue);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => reject(putRequest.error);
          });
          updatedRecords.push(1);
        }
      }

      return updatedRecords.length;
    } catch (error) {
      console.error("updateByIndexOrKey error:", error);
      throw error;
    }
  }
}
